import React, { useEffect, useState } from 'react'
import { EmblaOptionsType } from 'embla-carousel'
import { usePrevNextButtons } from '../utils/EmblaCarouselHooks'
import {
  PrevButton,
  NextButton
} from '../utils/EmblaCarouselButtons'
import useEmblaCarousel from 'embla-carousel-react'
import { useNavigate } from 'react-router-dom'
import { buildFileUrl } from '../../config/strapi'

interface NewsItem {
  id: number
  dataAtualizacao: string
  image: {
    id: number
    url: string
    alternativeText?: string
    formats?: {
      thumbnail?: { url: string }
      small?: { url: string }
      medium?: { url: string }
      large?: { url: string }
    }
  }
  header: {
    id: number
    title: string
    description: string
    type: string
  }
  noticiaPage: {
    id: number
    text: string
    autor?: string
    mainImage?: any
    secImage?: any
  }
}

interface NewsCarouselProps {
  news: NewsItem[]
  options?: EmblaOptionsType
}

export function NewsCarousel({ news, options }: NewsCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel(options)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const navigate = useNavigate()

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  } = usePrevNextButtons(emblaApi)

  // Atualizar índice ativo quando o carrossel muda
  useEffect(() => {
    if (!emblaApi) return

    const onSelect = () => {
      setActiveIndex(emblaApi.selectedScrollSnap())
    }

    const onScroll = () => {
      setIsTransitioning(true)
    }

    const onSettled = () => {
      // Aguardar um pouco para a transição terminar completamente
      setTimeout(() => {
        setIsTransitioning(false)
      }, 0)
    }

    emblaApi.on('select', onSelect)
    emblaApi.on('scroll', onScroll)
    emblaApi.on('settle', onSettled)
    onSelect() // Definir índice inicial

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('scroll', onScroll)
      emblaApi.off('settle', onSettled)
    }
  }, [emblaApi])

  // Passagem automática a cada 5 segundos
  useEffect(() => {
    if (!emblaApi || !emblaApi.canScrollNext()) return

    const interval = setInterval(() => {
      if (emblaApi.canScrollNext()) {
        emblaApi.scrollNext()
      } else {
        // Se chegou ao fim, volta ao início (loop)
        emblaApi.scrollTo(0)
      }
    }, 6000)

    return () => clearInterval(interval)
  }, [emblaApi])

  const handleNewsClick = (newsItem: NewsItem) => {
    // Passa a origem 'home' para que o botão "Voltar" saiba para onde retornar
    navigate(`/noticia/${newsItem.id}`, { state: { from: 'home' } })
  }

  if (!news || news.length === 0) {
    return null
  }

  return (
    <section style={{
      backgroundColor: '#ffffff',
      padding: 'clamp(14px, 2vw, 20px) 0 clamp(3px, 1vw, 5px) 0'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 clamp(1px, 1vw, 1px)'
      }}>
        <h2 style={{
          fontSize: 'clamp(24px, 5vw, 36px)',
          fontWeight: 'bold',
          textAlign: 'center',
          color: '#1f2937',
          marginBottom: 'clamp(32px, 6vw, 64px)'
        }}>
          Acompanhe as notícias
        </h2>
        
        <div style={{ position: 'relative' }}>
          <div style={{ 
            overflow: 'hidden',
            maxWidth: 'clamp(300px, 80vw, 700px)',
            margin: '0 auto'
          }} ref={emblaRef}>
            <div style={{
              display: 'flex',
              userSelect: 'none',
              WebkitTouchCallout: 'none',
              WebkitTapHighlightColor: 'transparent'
            }}>
              {news.map((newsItem, index) => (
                <div 
                  key={newsItem.id} 
                  style={{
                    flex: '0 0 100%',
                    minWidth: 0,
                    padding: '0 clamp(16px, 3vw, 24px)'
                  }}
                  onClick={() => handleNewsClick(newsItem)}
                >
                  <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    border: (index === activeIndex && isTransitioning) ? '3px solid #007bff' : '3px solid transparent'
                  }}>
                    <div style={{
                      position: 'relative',
                      height: 'clamp(180px, 40vw, 240px)',
                      overflow: 'hidden'
                    }}>
                      {newsItem.image?.url ? (
                        <img
                          src={buildFileUrl(newsItem.image.url)}
                          alt={newsItem.image.alternativeText || newsItem.header.title}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          backgroundColor: '#d1d5db',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <span style={{
                            color: '#6b7280',
                            fontSize: 'clamp(14px, 3vw, 16px)'
                          }}>
                            Sem imagem
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div style={{
                      padding: 'clamp(16px, 4vw, 24px)'
                    }}>
                      <h3 style={{
                        fontSize: 'clamp(18px, 4vw, 24px)',
                        fontWeight: 'bold',
                        color: '#1f2937',
                        marginBottom: 'clamp(12px, 2vw, 16px)',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: '1.3'
                      }}>
                        {newsItem.header.title}
                      </h3>
                      <p style={{
                        color: '#6b7280',
                        fontSize: 'clamp(14px, 3vw, 16px)',
                        marginBottom: 'clamp(12px, 2vw, 16px)',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: '1.4'
                      }}>
                        {newsItem.header.description}
                      </p>
                      <div style={{ marginTop: '16px' }}>
                        <span style={{
                          color: '#007bff',
                          fontSize: 'clamp(14px, 3vw, 16px)',
                          fontWeight: '500',
                          transition: 'color 0.2s ease'
                        }}>
                          Saiba mais
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Botões de navegação posicionados sobre o carrossel */}
          <PrevButton 
            onClick={onPrevButtonClick} 
            disabled={prevBtnDisabled}
            style={{
              position: 'absolute',
              left: 'clamp(16px, 4vw, 24px)',
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: prevBtnDisabled ? '#d1d5db' : '#007bff',
              color: '#ffffff',
              borderRadius: '50%',
              width: 'clamp(32px, 6vw, 40px)',
              height: 'clamp(32px, 6vw, 40px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              cursor: prevBtnDisabled ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              zIndex: 10,
              transition: 'all 0.2s ease'
            }}
          />
          
          <NextButton 
            onClick={onNextButtonClick} 
            disabled={nextBtnDisabled}
            style={{
              position: 'absolute',
              right: 'clamp(16px, 4vw, 24px)',
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: nextBtnDisabled ? '#d1d5db' : '#007bff',
              color: '#ffffff',
              borderRadius: '50%',
              width: 'clamp(32px, 6vw, 40px)',
              height: 'clamp(32px, 6vw, 40px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              cursor: nextBtnDisabled ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              zIndex: 10,
              transition: 'all 0.2s ease'
            }}
          />
        </div>
      </div>
    </section>
  )
} 